import React from "react";
import { Divider, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { Checkbox, Modal } from "components";
import { useCurrentCase } from "duck";
import { DentalChartEnum } from "requests";
import { getJaw } from "../../../../../dental-charting/duck";
import { FormValues } from "../../duck";

interface SelectionModalProps {
  onClose(): void;
  isOpen: boolean;
  fieldName: keyof FormValues;
}

const SelectionModal: React.FC<SelectionModalProps> = ({
  onClose,
  isOpen,
  fieldName
}) => {
  const { setValue, watch } = useFormContext<FormValues>();
  const defaultValue = watch(fieldName);
  const isRemainingDentitionChecked = defaultValue === "Remaining dentition";
  const selectedValues =
    defaultValue && !isRemainingDentitionChecked
      ? defaultValue.split(", ").map(Number)
      : [];

  const { species, milkOrAdultTeeth } = useCurrentCase(medCase => ({
    species: medCase.data.client.patient.species || null,
    milkOrAdultTeeth:
      medCase.data.client.patient.generalAssessment?.dentalChart || null
  }));

  const jaw = getJaw(species, milkOrAdultTeeth);

  if (!jaw) {
    return null;
  }

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      title="Please select all teeth that apply:"
    >
      <SimpleGrid columns={4}>
        {Object.entries(jaw).map(([arcadeNumber, teethNumbers]) => (
          <Flex key={arcadeNumber} direction="column" rowGap="2">
            <Checkbox
              label={
                <Text as="span" fontWeight="semibold">
                  {milkOrAdultTeeth === DentalChartEnum.Deciduous
                    ? +arcadeNumber + 4
                    : arcadeNumber}
                  00s
                </Text>
              }
              isChecked={teethNumbers.every(number =>
                selectedValues.includes(number)
              )}
              onChange={event => {
                const { checked } = event.target;
                if (checked) {
                  setValue(
                    fieldName,
                    [...selectedValues, ...teethNumbers].join(", ")
                  );
                } else {
                  setValue(
                    fieldName,
                    selectedValues
                      .filter(value => !teethNumbers.includes(value))
                      .join(", ")
                  );
                }
              }}
            />

            {teethNumbers.map(number => (
              <Checkbox
                key={number}
                label={number}
                isChecked={selectedValues.includes(number)}
                onChange={event => {
                  const { checked } = event.target;
                  if (checked) {
                    setValue(fieldName, [...selectedValues, number].join(", "));
                  } else {
                    setValue(
                      fieldName,
                      selectedValues
                        .filter(value => value !== number)
                        .join(", ")
                    );
                  }
                }}
              />
            ))}
          </Flex>
        ))}
      </SimpleGrid>

      <Divider my={6} h="1px" borderColor="#D0D1D3" />

      <Checkbox
        label="Remaining dentition"
        isChecked={isRemainingDentitionChecked}
        onChange={event =>
          event.target.checked
            ? setValue(fieldName, "Remaining dentition")
            : setValue(fieldName, "")
        }
      />
    </Modal>
  );
};

export default SelectionModal;
